import React, { useEffect, useState, useMemo } from 'react';
import { reportsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, FileDown, Upload, Users, Calendar, RefreshCw, Layers } from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Per-source brand colour. Used both for the sub-tab accents and the
// stacked-bar segments so the eye locks onto the same colour for the same
// portal across both views. Tailwind utility strings kept inline rather
// than constructed dynamically — class purger would otherwise drop them.
const SOURCE_COLORS = {
  naukri:   { bar: 'bg-orange-500',  text: 'text-orange-700',  bg: 'bg-orange-50',  border: 'border-orange-200' },
  linkedin: { bar: 'bg-sky-500',     text: 'text-sky-700',     bg: 'bg-sky-50',     border: 'border-sky-200' },
  shine:    { bar: 'bg-indigo-500',  text: 'text-indigo-700',  bg: 'bg-indigo-50',  border: 'border-indigo-200' },
  foundit:  { bar: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200' },
  other:    { bar: 'bg-slate-400',   text: 'text-slate-700',   bg: 'bg-slate-50',   border: 'border-slate-200' },
};

// Multi-source recruiter-wise upload report. Powers Reports → Source Uploads.
// One backend call (/api/reports/source-uploads) returns counts split by
// portal (Naukri / LinkedIn / Shine / Foundit / Other) — both the per-source
// table view and the stacked-bar mix are derived from the same payload.
const SourceUploadsCard = () => {
  const [period, setPeriod] = useState('all_time');
  const [activeSource, setActiveSource] = useState('naukri');
  // 'volume' = absolute count chart (default). 'conversion' = each segment
  // is the JOINED count from that source. Same backend payload feeds both.
  const [chartMode, setChartMode] = useState('volume');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsApi.sourceUploads(period);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load Source Uploads report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // If the org has removed the 'naukri' default (or first-load), snap the
  // active sub-tab to the first source returned by the backend so we never
  // show an empty table for a portal that doesn't exist in this org.
  useEffect(() => {
    if (!data?.sources?.length) return;
    const slugs = data.sources.map((s) => s.slug);
    if (!slugs.includes(activeSource)) {
      setActiveSource(slugs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const sources = data?.sources || [];
  const totalsBySource = data?.totals_by_source || {};
  const joinedBySource = data?.joined_totals_by_source || {};
  const perSourceMeta = data?.per_source_meta || {};

  // Conversion rate by source (org-wide): joined / uploads * 100. Guard
  // against div-by-zero — show "—" instead of NaN when there are no
  // uploads for a portal yet.
  const conversionBySource = useMemo(() => {
    const out = {};
    sources.forEach((s) => {
      const up = totalsBySource[s.slug] || 0;
      const jn = joinedBySource[s.slug] || 0;
      out[s.slug] = up > 0 ? (jn / up) * 100 : null;
    });
    return out;
  }, [sources, totalsBySource, joinedBySource]);

  // Rows for the table that respects the active sub-tab. We DON'T filter
  // out zeros — a recruiter with 0 Naukri uploads is still useful info on
  // the Naukri tab ("nothing this period").
  const tableRows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows]
      .filter((r) => (r.sources?.[activeSource] || 0) > 0)
      .sort((a, b) => (b.sources?.[activeSource] || 0) - (a.sources?.[activeSource] || 0));
  }, [data, activeSource]);

  // Top recruiters by overall volume for the stacked-bar mix view. Capped
  // at 12 so the chart stays scannable even in orgs with 50+ recruiters.
  // In conversion mode we re-sort by total_joined so the recruiters who
  // actually close come to the top regardless of who uploads the most.
  const mixRows = useMemo(() => {
    if (!data?.rows) return [];
    const sorted = [...data.rows].sort((a, b) => {
      if (chartMode === 'conversion') {
        return (b.total_joined || 0) - (a.total_joined || 0);
      }
      return (b.total || 0) - (a.total || 0);
    });
    return sorted.slice(0, 12);
  }, [data, chartMode]);

  const grandTotalForBar = useMemo(() => {
    const key = chartMode === 'conversion' ? 'total_joined' : 'total';
    const maxRowTotal = mixRows.reduce((m, r) => Math.max(m, r[key] || 0), 0);
    return maxRowTotal || 1;
  }, [mixRows, chartMode]);

  // CSV export of the currently active source view — preserves the
  // recruiter ↔ portal attribution lineage in a single click.
  const handleExport = () => {
    if (!tableRows.length) return;
    const header = [
      'Recruiter', 'Email', 'Role',
      `${activeSource} Uploads`,
      `${activeSource} Joined`,
      'Conversion %',
      'First Upload', 'Last Upload',
    ];
    const rows = tableRows.map((r) => {
      const meta = r.per_source_meta?.[activeSource] || {};
      const uploads = r.sources?.[activeSource] || 0;
      const joined = r.joined_by_source?.[activeSource] || 0;
      const conv = uploads > 0 ? ((joined / uploads) * 100).toFixed(1) : '';
      return [
        r.name,
        r.email || '',
        r.role || '',
        uploads,
        joined,
        conv,
        meta.first_upload_at ? format(new Date(meta.first_upload_at), 'yyyy-MM-dd HH:mm') : '',
        meta.last_upload_at ? format(new Date(meta.last_upload_at), 'yyyy-MM-dd HH:mm') : '',
      ];
    });
    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeSource}-uploads-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${activeSource} report exported`);
  };

  const activeMeta = perSourceMeta[activeSource] || {};

  return (
    <Card data-testid="reports-source-uploads-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-teal-600" />
              Source Uploads — Recruiter-wise
            </CardTitle>
            <CardDescription>
              Bulk-uploaded resumes grouped by portal (matched on the original
              filename prefix). Switch sub-tabs for a portal-specific table or
              scan the mix bar below for each recruiter's sourcing profile.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="source-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_time">All time</SelectItem>
                <SelectItem value="current_month">This month</SelectItem>
                <SelectItem value="last_month">Last month</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="source-refresh-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={loading || !tableRows.length}
              data-testid="source-export-btn"
            >
              <FileDown className="w-4 h-4 mr-1" />
              Export {activeSource}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Per-source summary chips. Each chip is the count for that portal
            across the entire scoped subtree; click-through doubles as
            sub-tab activation so chips are interactive too. */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="source-summary-chips">
          {sources.map((s) => {
            const tone = SOURCE_COLORS[s.slug] || SOURCE_COLORS.other;
            const active = activeSource === s.slug;
            const up = totalsBySource[s.slug] || 0;
            const jn = joinedBySource[s.slug] || 0;
            const conv = conversionBySource[s.slug];
            return (
              <button
                key={s.slug}
                type="button"
                onClick={() => setActiveSource(s.slug)}
                className={`group rounded-lg border px-3 py-2 text-left transition flex items-center gap-2.5 ${
                  active
                    ? `${tone.bg} ${tone.border} ring-2 ring-offset-1 ring-teal-400`
                    : `bg-white ${tone.border} hover:${tone.bg}`
                }`}
                data-testid={`source-summary-chip-${s.slug}`}
              >
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${tone.bar}`} />
                <div className="min-w-0">
                  <div className={`text-[10px] uppercase tracking-wide font-bold ${tone.text}`}>{s.label}</div>
                  <div className="text-lg font-bold tabular-nums text-slate-900">
                    {up}
                  </div>
                  {/* Conversion line — "→ N joined (X%)". Surfaces the
                      single most useful follow-up question on every chip. */}
                  <div className="text-[10px] text-slate-500 tabular-nums" data-testid={`source-chip-conv-${s.slug}`}>
                    →{' '}
                    <span className="font-semibold text-emerald-700">{jn}</span> joined
                    {conv !== null ? (
                      <span className="text-slate-400"> · {conv.toFixed(1)}%</span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
          <div className="ml-auto text-xs text-slate-500 flex items-center gap-3" data-testid="source-grand-total">
            <div>
              <span className="text-slate-400">All uploads:</span>{' '}
              <span className="font-bold tabular-nums text-slate-900">{data?.total || 0}</span>
            </div>
            <div>
              <span className="text-slate-400">Joined:</span>{' '}
              <span className="font-bold tabular-nums text-emerald-700">{data?.total_joined || 0}</span>
              {data?.total ? (
                <span className="text-slate-400"> · {((data.total_joined / data.total) * 100).toFixed(1)}%</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Chart-mode toggle — Volume (default) vs Conversion. Drives both
            the stacked-bar segments and how mixRows are computed. */}
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className="text-slate-500 font-medium">Mix by:</span>
          <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="source-chart-mode-toggle">
            <button
              type="button"
              onClick={() => setChartMode('volume')}
              className={`px-3 py-1 text-xs font-semibold transition ${
                chartMode === 'volume'
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="source-chart-mode-volume"
            >
              Volume
            </button>
            <button
              type="button"
              onClick={() => setChartMode('conversion')}
              className={`px-3 py-1 text-xs font-semibold transition ${
                chartMode === 'conversion'
                  ? 'bg-emerald-700 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="source-chart-mode-conversion"
            >
              Conversion (joined)
            </button>
          </div>
          <span className="text-[10px] text-slate-400 hidden sm:inline">
            {chartMode === 'volume'
              ? 'Shows uploads — total volume per recruiter.'
              : 'Shows only candidates that reached Joined — which portal actually closes.'}
          </span>
        </div>

        {/* Per-recruiter mix bar (top 12). In Volume mode each bar's width
            tracks total uploads and segments split by portal share. In
            Conversion mode the bar tracks joined uploads only — the
            recruiters who turn portals into closures float to the top. */}
        {!loading && !error && mixRows.length > 0 ? (
          <div className="mb-5" data-testid="source-mix-chart">
            <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
              <span>Source Mix · Top {mixRows.length}</span>
              <span className="text-[10px] font-normal text-slate-400">
                ({chartMode === 'conversion'
                  ? 'bar width = joined uploads; segments = portal split of those closures'
                  : 'bar width = total uploads; segments = portal share'})
              </span>
            </div>
            <div className="space-y-1.5">
              {mixRows.map((r) => {
                const rowTotalKey = chartMode === 'conversion' ? 'total_joined' : 'total';
                const segMap = chartMode === 'conversion' ? r.joined_by_source : r.sources;
                const rowTotal = r[rowTotalKey] || 0;
                const widthPct = (rowTotal / grandTotalForBar) * 100;
                const upTotal = r.total || 0;
                const convRate = upTotal > 0 ? ((r.total_joined || 0) / upTotal) * 100 : null;
                return (
                  <div
                    key={r.user_id}
                    className="flex items-center gap-2 text-xs"
                    data-testid={`source-mix-row-${r.user_id}`}
                  >
                    <div className="w-36 min-w-[9rem] truncate font-medium text-slate-800" title={r.name}>
                      {r.name}
                    </div>
                    <div className="flex-1 h-5 rounded bg-slate-100 overflow-hidden relative">
                      <div className="h-full flex" style={{ width: `${widthPct}%` }}>
                        {sources.map((s) => {
                          const count = (segMap || {})[s.slug] || 0;
                          if (count <= 0 || rowTotal <= 0) return null;
                          const segWidth = (count / rowTotal) * 100;
                          const tone = SOURCE_COLORS[s.slug] || SOURCE_COLORS.other;
                          return (
                            <div
                              key={s.slug}
                              className={`${tone.bar} h-full`}
                              style={{ width: `${segWidth}%` }}
                              title={`${s.label}: ${count}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                    {/* Right-side metric column. Volume mode shows total;
                        Conversion mode shows joined + conv-rate so the
                        manager doesn't have to recompute. */}
                    {chartMode === 'conversion' ? (
                      <div className="flex items-baseline gap-1 w-24 justify-end tabular-nums">
                        <span className="font-semibold text-emerald-700">{rowTotal}</span>
                        {convRate !== null ? (
                          <span className="text-[10px] text-slate-400">· {convRate.toFixed(0)}%</span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="w-12 text-right text-slate-700 tabular-nums font-semibold">
                        {rowTotal}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Sub-tabs — shadcn Tabs gives keyboard nav + roving focus. We
            don't render TabsContent because the table below is driven by
            the activeSource state directly. */}
        <Tabs value={activeSource} onValueChange={setActiveSource} className="mb-3" data-testid="source-subtabs">
          <TabsList>
            {sources.map((s) => (
              <TabsTrigger
                key={s.slug}
                value={s.slug}
                data-testid={`source-subtab-${s.slug}`}
              >
                {s.label}
                <span className="ml-1.5 text-[10px] opacity-70">{totalsBySource[s.slug] || 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Active-source meta strip — quick reassurance that the period
            and source the user sees match what they expected. */}
        <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-slate-500" data-testid="source-active-meta">
          <Badge variant="outline" className={`${SOURCE_COLORS[activeSource]?.bg} ${SOURCE_COLORS[activeSource]?.text} capitalize`}>
            {activeSource}
          </Badge>
          <span>·</span>
          <span>
            <Users className="w-3 h-3 inline mr-1" />
            {tableRows.length} recruiter{tableRows.length === 1 ? '' : 's'}
          </span>
          <span>·</span>
          <span>
            <Upload className="w-3 h-3 inline mr-1" />
            {totalsBySource[activeSource] || 0} upload{(totalsBySource[activeSource] || 0) === 1 ? '' : 's'}
          </span>
          {activeMeta.last_upload_at ? (
            <>
              <span>·</span>
              <span>
                <Calendar className="w-3 h-3 inline mr-1" />
                last upload {format(new Date(activeMeta.last_upload_at), 'MMM dd, HH:mm')}
              </span>
            </>
          ) : null}
        </div>

        {error ? (
          <div className="py-10 text-center text-sm text-rose-600" data-testid="source-error">
            {error}
          </div>
        ) : loading ? (
          <div className="py-10 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : !tableRows.length ? (
          <div className="py-10 text-center text-sm text-slate-400 italic" data-testid="source-empty">
            No {activeSource} uploads in this period.
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-[30%]">Recruiter</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">
                    <span className="capitalize">{activeSource}</span> Uploads
                  </TableHead>
                  <TableHead className="text-right">Joined</TableHead>
                  <TableHead className="text-right">Conv %</TableHead>
                  <TableHead>Last upload</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((r, idx) => {
                  const meta = r.per_source_meta?.[activeSource] || {};
                  const uploads = r.sources?.[activeSource] || 0;
                  const joined = r.joined_by_source?.[activeSource] || 0;
                  const conv = uploads > 0 ? (joined / uploads) * 100 : null;
                  return (
                    <TableRow key={r.user_id} data-testid={`source-row-${idx}`}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">{r.name}</span>
                          {r.email ? (
                            <span className="text-xs text-slate-500">{r.email}</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {(r.role || 'unknown').replace('_', ' ')}
                        </Badge>
                        {r.is_active === false ? (
                          <Badge variant="outline" className="ml-1.5 bg-rose-50 text-rose-700 border-rose-200">
                            inactive
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold tabular-nums ${SOURCE_COLORS[activeSource]?.text || 'text-slate-700'}`}
                        data-testid={`source-row-count-${idx}`}
                      >
                        {uploads}
                      </TableCell>
                      <TableCell
                        className="text-right font-semibold tabular-nums text-emerald-700"
                        data-testid={`source-row-joined-${idx}`}
                      >
                        {joined}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums text-slate-600"
                        data-testid={`source-row-conv-${idx}`}
                      >
                        {conv === null ? '—' : `${conv.toFixed(1)}%`}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {meta.last_upload_at ? format(new Date(meta.last_upload_at), 'MMM dd, yyyy HH:mm') : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Footer total row — sums per active source. */}
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell colSpan={2} className="text-slate-700">Total</TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${SOURCE_COLORS[activeSource]?.text || 'text-slate-700'}`}
                    data-testid="source-footer-total"
                  >
                    {totalsBySource[activeSource] || 0}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700" data-testid="source-footer-joined">
                    {joinedBySource[activeSource] || 0}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-600" data-testid="source-footer-conv">
                    {(totalsBySource[activeSource] || 0) > 0
                      ? `${(((joinedBySource[activeSource] || 0) / totalsBySource[activeSource]) * 100).toFixed(1)}%`
                      : '—'}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {data?.generated_at ? (
          <div className="mt-3 text-[11px] text-slate-400">
            Generated {format(new Date(data.generated_at), 'MMM dd, HH:mm')}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default SourceUploadsCard;
